import React, { useState, useRef, useEffect } from "react";
import { Link, useParams } from "react-router-dom";

// const API_BASE = "https://hopeful-cosmic-koala.ngrok-free.app/api";
const API_BASE = "/api";

// ── Renders Quill Delta JSON (contentJson) as React elements ───────────────
const renderQuillContent = (contentJson) => {
  try {
    const parsed = JSON.parse(contentJson);
    const text = parsed.ops
      .map((op) => op.insert)
      .join("")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
    return text
      .split("\n\n")
      .filter(Boolean)
      .map((para, i) => (
        <p key={i} className="message-paragraph">{para}</p>
      ));
  } catch {
    return <p>Content unavailable.</p>;
  }
};

const PastorMessageDetail = () => {
  const { slug } = useParams();

  const [message, setMessage]         = useState(null);
  const [related, setRelated]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [isPlaying, setIsPlaying]     = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration]       = useState(0);
  const audioRef = useRef(null);

  // ── Fetch message detail + related ────────────────────────────────────────
  useEffect(() => {
    const fetchMessage = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_BASE}/pastor-messages/${slug}/`, {
          headers: {
            "Content-Type": "application/json",
            "ngrok-skip-browser-warning": "69420",
          },
        });
        if (!response.ok) throw new Error(`Failed to fetch message: ${response.status}`);
        const data = await response.json();
        setMessage(data);

        // Fetch related via same category
        if (data.category?.slug) {
          const relRes = await fetch(
            `${API_BASE}/pastor-messages/?category=${data.category.slug}&page_size=4`,
            { headers: { "ngrok-skip-browser-warning": "69420" } }
          );
          if (relRes.ok) {
            const relData = await relRes.json();
            const results = Array.isArray(relData) ? relData : (relData.results ?? []);
            setRelated(results.filter((m) => m.slug !== slug).slice(0, 3));
          }
        }
      } catch (err) {
        console.error("Fetch error:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchMessage();
  }, [slug]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const formatDate = (d) => {
    if (!d) return "";
    return new Date(d).toLocaleDateString("en-US", {
      month: "long", day: "numeric", year: "numeric",
    });
  };

  const formatTime = (time) => {
    if (isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  // ── Audio ─────────────────────────────────────────────────────────────────
  const togglePlay = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate    = () => setCurrentTime(audioRef.current.currentTime);
  const handleLoadedMetadata = () => setDuration(audioRef.current.duration);

  const handleSeek = (e) => {
    const seekTime = (e.target.value / 100) * duration;
    audioRef.current.currentTime = seekTime;
    setCurrentTime(seekTime);
  };

  const downloadAudio = () => {
    const link = document.createElement("a");
    link.href = message.audio_url;
    link.download = `${message.title}.mp3`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) return (
    <>
      <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css" rel="stylesheet" />
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <div style={{ width: 44, height: 44, border: "4px solid #e8f4fd", borderTopColor: "#0b1c6b", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </>
  );

  // ── Error ─────────────────────────────────────────────────────────────────
  if (error || !message) return (
    <>
      <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css" rel="stylesheet" />
      <div style={{ textAlign: "center", padding: "80px 20px" }}>
        <div style={{ fontSize: "3.5rem", marginBottom: 16 }}>⚠️</div>
        <h4 style={{ color: "#0b1c6b" }}>Could not load message</h4>
        <p style={{ color: "#6b7280" }}>{error || "Message not found."}</p>
        <Link to="/pastor-messages" className="btn btn-outline-primary rounded-pill px-4 mt-3">
          ← Back to Messages
        </Link>
      </div>
    </>
  );

  const hasAudio = Boolean(message.audio_url);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css" rel="stylesheet" />
      <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/font/bootstrap-icons.min.css" />

      <div className="message-detail-page">

        {/* BREADCRUMB */}
        <section className="bg-light py-3">
          <div className="container">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to="/" className="text-decoration-none">
                    <i className="bi bi-house-door me-1"></i>Home
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/pastor-messages" className="text-decoration-none">Messages</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {message.category?.name || "Teachings"}
                </li>
              </ol>
            </nav>
          </div>
        </section>

        {/* HERO IMAGE */}
        <section className="message-hero">
          {message.cover_image ? (
            <img src={message.cover_image} alt={message.title} className="w-100" />
          ) : (
            <div style={{ width: "100%", height: "100%", background: "linear-gradient(135deg,#0b1c6b,#159EEC)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "6rem" }}>
              🎙️
            </div>
          )}
          <div className="hero-gradient"></div>
        </section>

        {/* MESSAGE HEADER */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-10 col-xl-8">

                {/* Category badge */}
                {message.category && (
                  <div className="mb-3">
                    <span className="badge bg-info bg-opacity-10 text-info text-uppercase px-3 py-2">
                      <i className="bi bi-bookmark-fill me-2"></i>
                      {message.category.name}
                    </span>
                  </div>
                )}

                <h1 className="display-4 fw-bold text-primary mb-4">{message.title}</h1>

                <div className="d-flex flex-wrap align-items-center gap-4 mb-4 pb-4 border-bottom">
                  {message.pastor_name && (
                    <div className="d-flex align-items-center text-muted">
                      <i className="bi bi-person-circle fs-5 me-2"></i>
                      <span>{message.pastor_name}</span>
                    </div>
                  )}
                  <div className="d-flex align-items-center text-muted">
                    <i className="bi bi-calendar3 fs-5 me-2"></i>
                    <span>{formatDate(message.published_date)}</span>
                  </div>
                  {message.audio_duration && (
                    <div className="d-flex align-items-center text-muted">
                      <i className="bi bi-clock fs-5 me-2"></i>
                      <span>{message.audio_duration}</span>
                    </div>
                  )}
                  {message.scripture_reference && (
                    <div className="d-flex align-items-center text-primary fw-bold">
                      <i className="bi bi-book fs-5 me-2"></i>
                      <span>{message.scripture_reference}</span>
                    </div>
                  )}
                </div>

                <div className="d-flex flex-wrap gap-4 mb-4">
                  {message.listener_count > 0 && (
                    <div className="d-flex align-items-center text-muted">
                      <i className="bi bi-headphones fs-5 me-2"></i>
                      <span>{message.listener_count} listeners</span>
                    </div>
                  )}
                  {message.download_count > 0 && (
                    <div className="d-flex align-items-center text-muted">
                      <i className="bi bi-download fs-5 me-2"></i>
                      <span>{message.download_count} downloads</span>
                    </div>
                  )}
                </div>

              </div>
            </div>
          </div>
        </section>

        {/* AUDIO PLAYER — hidden if no audio_url */}
        {hasAudio && (
          <section className="py-4 bg-light sticky-player">
            <div className="container">
              <div className="row justify-content-center">
                <div className="col-lg-10 col-xl-8">
                  <div className="audio-player-card card border-0 shadow-lg rounded-4 p-4">
                    <div className="row align-items-center g-3">
                      <div className="col-auto">
                        <button onClick={togglePlay} className="btn btn-primary rounded-circle play-btn">
                          <i className={`bi ${isPlaying ? "bi-pause-fill" : "bi-play-fill"} fs-3`}></i>
                        </button>
                      </div>
                      <div className="col">
                        <div className="d-flex align-items-center gap-3 mb-2">
                          <span className="text-muted small">{formatTime(currentTime)}</span>
                          <input
                            type="range"
                            className="form-range flex-grow-1"
                            min="0"
                            max="100"
                            value={duration ? (currentTime / duration) * 100 : 0}
                            onChange={handleSeek}
                          />
                          <span className="text-muted small">{formatTime(duration)}</span>
                        </div>
                        <h6 className="mb-0 fw-bold text-primary">{message.title}</h6>
                      </div>
                      <div className="col-auto">
                        <button onClick={downloadAudio} className="btn btn-outline-primary rounded-pill px-4">
                          <i className="bi bi-download me-2"></i>Download
                        </button>
                      </div>
                    </div>

                    <audio
                      ref={audioRef}
                      src={message.audio_url}
                      onTimeUpdate={handleTimeUpdate}
                      onLoadedMetadata={handleLoadedMetadata}
                      onEnded={() => setIsPlaying(false)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* MESSAGE CONTENT */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-10 col-xl-8">

                {/* Share Buttons */}
                <div className="d-flex align-items-center gap-3 mb-5 pb-4 border-bottom">
                  <span className="fw-bold text-muted me-2">Share:</span>
                  <button className="btn btn-outline-primary btn-sm rounded-circle share-btn">
                    <i className="bi bi-facebook"></i>
                  </button>
                  <button className="btn btn-outline-primary btn-sm rounded-circle share-btn">
                    <i className="bi bi-twitter-x"></i>
                  </button>
                  <button className="btn btn-outline-primary btn-sm rounded-circle share-btn">
                    <i className="bi bi-whatsapp"></i>
                  </button>
                  <button className="btn btn-outline-primary btn-sm rounded-circle share-btn">
                    <i className="bi bi-linkedin"></i>
                  </button>
                  <button className="btn btn-outline-primary btn-sm rounded-circle share-btn">
                    <i className="bi bi-link-45deg"></i>
                  </button>
                </div>

                {/* Rendered Quill content */}
                <div className="message-content">
                  {renderQuillContent(message.contentJson)}
                </div>

              </div>
            </div>
          </div>
        </section>

        {/* RELATED MESSAGES */}
        {related.length > 0 && (
          <section className="py-5 bg-light">
            <div className="container">
              <div className="row justify-content-center">
                <div className="col-lg-10 col-xl-8">
                  <h3 className="fw-bold text-primary mb-4">
                    <i className="bi bi-mic-fill me-2"></i>More Messages
                  </h3>
                  <div className="row g-4">
                    {related.map((rel) => (
                      <div className="col-md-4" key={rel.id}>
                        <Link to={`/pastor-message/${rel.slug}`} className="text-decoration-none">
                          <div className="card border-0 shadow-sm rounded-4 h-100 related-card">
                            {rel.cover_image ? (
                              <img
                                src={rel.cover_image}
                                className="card-img-top"
                                alt={rel.title}
                                style={{ height: "180px", objectFit: "cover" }}
                              />
                            ) : (
                              <div style={{ height: 180, background: "linear-gradient(135deg,#0b1c6b,#159EEC)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "3rem" }}>
                                🎙️
                              </div>
                            )}
                            <div className="card-body">
                              {rel.category && (
                                <span className="badge bg-info bg-opacity-10 text-info text-uppercase mb-2" style={{ fontSize: "10px" }}>
                                  {rel.category.name}
                                </span>
                              )}
                              <h6 className="card-title fw-bold text-primary mb-2">{rel.title}</h6>
                              <p className="card-text text-muted mb-0" style={{ fontSize: "14px" }}>
                                <i className="bi bi-calendar3 me-1"></i>
                                {formatDate(rel.published_date)}
                              </p>
                            </div>
                          </div>
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* BACK TO MESSAGES */}
        <section className="py-4 bg-white">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-10 col-xl-8">
                <Link to="/pastor-messages" className="btn btn-outline-primary rounded-pill px-4">
                  <i className="bi bi-arrow-left me-2"></i>Back to All Messages
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* SOCIAL */}
        <section className="social-section py-5 text-center text-white">
          <div className="container py-4">
            <h2 className="display-5 fw-bold mb-3">Follow Us</h2>
            <p className="fs-6 mb-4 mx-auto" style={{ maxWidth: "600px" }}>
              Stay connected with Gospel City Church International through our social platforms.
            </p>
            <div className="d-flex justify-content-center gap-3 flex-wrap">
              <a href="https://www.linkedin.com/in/gospel-city-church-international-650a562a0/" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light rounded-circle social-icon">
                <i className="bi bi-linkedin fs-4"></i>
              </a>
              <a href="https://groups.imo.im/.5l9akqTOrYa5Tv4T4" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light rounded-circle social-icon">
            <i className="bi bi-chat-dots fs-4"></i>
            </a>
              <a href="https://x.com/GospelCityChur?t=x8dsFbFkghmYh02lV0FVxQ&s=03" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light rounded-circle social-icon">
                <i className="bi bi-twitter-x fs-4"></i>
              </a>
              <a href="https://whatsapp.com/channel/0029Vb769UyDzgTBAZZcb40F" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light rounded-circle social-icon">
                <i className="bi bi-whatsapp fs-4"></i>
              </a>
              <a href="https://youtube.com/@gospelcitychurchinternational?si=1pZx2ak4ss3b4HuU" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light rounded-circle social-icon">
                <i className="bi bi-youtube fs-4"></i>
              </a>
            </div>
          </div>
        </section>

        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Lexend:wght@300;400;500;600;700;800&display=swap');
          * { font-family: 'Lexend', sans-serif; }

          .message-hero {
            position: relative;
            height: 400px;
            overflow: hidden;
            background: #000;
          }
          .message-hero img { height: 100%; object-fit: cover; opacity: 0.85; }
          .hero-gradient {
            position: absolute; bottom: 0; left: 0; right: 0; height: 120px;
            background: linear-gradient(to bottom, transparent, rgba(255,255,255,1));
          }
          .breadcrumb-item a { color: #0b1c6b; }
          .breadcrumb-item a:hover { color: #159EEC; }

          /* Audio player */
          .sticky-player { position: sticky; top: 0; z-index: 1000; box-shadow: 0 4px 20px rgba(0,0,0,0.1); }
          .audio-player-card { background: linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%); }
          .play-btn { width: 70px; height: 70px; display: flex; align-items: center; justify-content: center; transition: all 0.3s; }
          .play-btn:hover { background: #159EEC; border-color: #159EEC; transform: scale(1.05); }
          .form-range::-webkit-slider-thumb { background: #0b1c6b; }
          .form-range::-moz-range-thumb { background: #0b1c6b; }
          .form-range::-webkit-slider-runnable-track { background: #e9ecef; height: 6px; border-radius: 3px; }

          /* Message content */
          .message-content { font-size: 18px; line-height: 1.9; color: #333; }
          .message-paragraph { margin-bottom: 1.5rem; }
          .message-content .message-paragraph:first-child {
            font-size: 1.2rem;
            font-weight: 500;
            color: #444;
          }

          /* Share buttons */
          .share-btn { width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; padding: 0; transition: all 0.3s; }
          .share-btn:hover { background: #0b1c6b; border-color: #0b1c6b; color: #fff; transform: translateY(-3px); }

          /* Related cards */
          .related-card { transition: transform 0.3s, box-shadow 0.3s; }
          .related-card:hover { transform: translateY(-5px); box-shadow: 0 15px 40px rgba(26,58,161,0.15) !important; }

          /* Colors */
          .text-primary { color: #1a3aa1 !important; }
          .text-info { color: #159EEC !important; }
          .bg-info { background-color: #159EEC !important; }
          .bg-primary { background-color: #0b1c6b !important; }
          .btn-primary { background: #0b1c6b; border-color: #0b1c6b; }
          .btn-primary:hover { background: #159EEC; border-color: #159EEC; }
          .btn-outline-primary { color: #0b1c6b; border-color: #0b1c6b; }
          .btn-outline-primary:hover { background: #0b1c6b; border-color: #0b1c6b; color: #fff; }
          .border-primary { border-color: #0b1c6b !important; }
          .social-section { background: linear-gradient(135deg,#0b1c6b,#159EEC); }
          .social-icon { width: 52px; height: 52px; display: flex; align-items: center; justify-content: center; transition: all 0.3s; }
          .social-icon:hover { background: rgba(255,255,255,0.3); transform: translateY(-4px) scale(1.1); }

          @media (max-width: 768px) {
            .message-hero { height: 250px; }
            .message-content { font-size: 16px; }
            .display-4 { font-size: 2rem; }
            .play-btn { width: 60px; height: 60px; }
            .sticky-player { position: relative; }
          }
        `}</style>
      </div>
    </>
  );
};

export default PastorMessageDetail;